/**
 * Runtime content for the app surface: 8 directions, 53 patterns, 66 exercises.
 *
 * One fetch per language, cached for the page's lifetime. The payloads are built
 * by `scripts/build-app-content.js` from the Notion "model v2" export — see that
 * file for which fields exist only there and not in `content/notion-v2/`.
 *
 * Every authored string here is already in the active language: the loader picks
 * the payload, so screens never branch on `lang` to read content.
 */
import React from 'react';
import { useLang } from '../i18n/LangContext';
import type { Lang } from '../i18n/detectLanguage';
import { Polarity } from './tokens';

export interface Direction {
    code: string;
    /** Short name in the active language, e.g. "Мислення" / "Cognition". */
    name: string;
    disp: string;
    /** One-line promise, shown under the name everywhere. */
    sub: string;
    tag: string;
    opis: string;
    insight: string;
    insightFrom: string;
    benefits: string[];
    measure: string[];
    theory: string[];
    traps: string[];
    helps: string[];
    /** [claim, source] — source is named, never a link list. */
    research: Array<[string, string]>;
}

export interface Pattern {
    code: string;
    name: string;
    pol: Polarity;
    dim: string;
    sub: string;
    aha: string;
    shows: string[];
    /** Mechanism prose, ~3 paragraphs. Complete for all 53. */
    mech: string[];
    /** "What changes when it works". Complete for all 53. */
    res: string[];
    /**
     * What a high or low number on this pattern means, in words.
     *
     * NOT rendered yet: the authored copy reads "higher = better" for both
     * polarities, while the quiz adds +100 to a blocker when the user endorses
     * it. Until that contradiction is settled, showing this would tell people
     * the opposite of what the bar next to it means.
     */
    note: string;
}

export interface Exercise {
    code: string;
    name: string;
    cls: string;
    load: string;
    min: number | null;
    /** Pattern codes in priority order — the first takes the rounding remainder. */
    pats: string[];
    /** Weight per pattern, positionally matching `pats`. Missing = all equal. */
    patW?: number[];
    track: string;
    /** What the exercise is, in 2–3 sentences. */
    desc: string;
    /** The steps, in order — what actually happens while doing it. */
    flow: string[];
    /** The mechanism, one paragraph: why this works rather than something else. */
    why: string;
    /** Asked before the first run. Empty means this one needs no question. */
    setupQ: string;
}

export interface AppContent {
    directions: Direction[];
    patterns: Pattern[];
    exercises: Exercise[];
    /** Lookups built once so screens never scan the arrays in render. */
    dirBy: Record<string, Direction>;
    patBy: Record<string, Pattern>;
    patsByDim: Record<string, Pattern[]>;
    exsByPattern: Record<string, Exercise[]>;
}

const urlFor = (lang: Lang) => `${process.env.PUBLIC_URL || ''}/data/content/app-content.${lang}.json`;

const cache: Map<Lang, Promise<AppContent>> = new Map();

function index(raw: { directions: Direction[]; patterns: Pattern[]; exercises: Exercise[] }): AppContent {
    const dirBy: Record<string, Direction> = {};
    const patBy: Record<string, Pattern> = {};
    const patsByDim: Record<string, Pattern[]> = {};
    const exsByPattern: Record<string, Exercise[]> = {};

    raw.directions.forEach((d) => {
        dirBy[d.code] = d;
        patsByDim[d.code] = [];
    });
    raw.patterns.forEach((p) => {
        patBy[p.code] = p;
        (patsByDim[p.dim] = patsByDim[p.dim] || []).push(p);
    });
    raw.exercises.forEach((e) => {
        (e.pats || []).forEach((code) => {
            (exsByPattern[code] = exsByPattern[code] || []).push(e);
        });
    });

    return { ...raw, dirBy, patBy, patsByDim, exsByPattern };
}

export function loadAppContent(lang: Lang): Promise<AppContent> {
    const hit = cache.get(lang);

    if (hit) return hit;

    const pending = fetch(urlFor(lang))
        .then((r) => {
            if (!r.ok) throw new Error(`app-content.${lang} ${r.status}`);
            return r.json();
        })
        .then(index)
        .catch((err) => {
            // Drop the cache so a transient failure does not poison every later
            // mount with the same rejected promise.
            cache.delete(lang);
            throw err;
        });

    cache.set(lang, pending);
    return pending;
}

/**
 * Switching language re-runs this effect, so the screens re-render against the
 * other payload without a reload. The previous language stays cached, which
 * makes switching back instant.
 */
export function useAppContent(): { content: AppContent | null; error: Error | null } {
    const { lang } = useLang();
    const [content, setContent] = React.useState<AppContent | null>(null);
    const [error, setError] = React.useState<Error | null>(null);

    React.useEffect(() => {
        let live = true;

        setError(null);
        loadAppContent(lang).then(
            (v) => live && setContent(v),
            (e) => live && setError(e),
        );
        return () => {
            live = false;
        };
    }, [lang]);

    return { content, error };
}
